import { useState, type SyntheticEvent } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { NetSuiteCustomerCandidate } from 'common-billing-server/types'

import { useBillingApi } from '../../api/billing/billing-context'
import { makeEditCustomerPath } from '../../routes/path'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'

export const ImportNetSuiteCustomer = () => {
    const billingApi = useBillingApi()
    const { navigate } = useStatefulNavigate()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [customerNumber, setCustomerNumber] = useState('')
    const [customers, setCustomers] = useState<NetSuiteCustomerCandidate[] | null>(null)
    const [lookingUp, setLookingUp] = useState(false)
    const [importingId, setImportingId] = useState<string>()
    const busy = lookingUp || importingId !== undefined

    async function lookup(event: SyntheticEvent<HTMLFormElement>) {
        event.preventDefault()
        if (busy || !customerNumber.trim()) return
        setLookingUp(true)
        setCustomers(null)
        removeFailedOperation('netsuite-customer')
        try {
            setCustomers(await billingApi.listNetsuiteCustomers({ customerNumber: customerNumber.trim() }))
        } catch (error) {
            addFailedOperation({ id: 'netsuite-customer', message: 'Failed looking up NetSuite customer', error })
        } finally {
            setLookingUp(false)
        }
    }

    async function importCustomer(customer: NetSuiteCustomerCandidate) {
        if (busy) return
        setImportingId(customer.netsuiteId)
        removeFailedOperation('netsuite-customer')
        try {
            const imported = await billingApi.importNetsuiteCustomers({
                netsuiteId: customer.netsuiteId,
                customerNumber: customer.customerNumber,
                accountUrl: customer.accountUrl,
            })
            navigate(makeEditCustomerPath(imported.id))
        } catch (error) {
            addFailedOperation({ id: 'netsuite-customer', message: 'Failed importing NetSuite customer', error })
        } finally {
            setImportingId(undefined)
        }
    }

    return (
        <Stack spacing={2}>
            <Typography>
                Find a customer by its exact NetSuite customer number. Import copies the name and number and enables
                future syncs. An existing customer with the same ID is reused.
            </Typography>
            <Typography color="text.secondary">
                If the customer already exists in Billing, correct its customer number first so import can reuse it.
            </Typography>
            <Stack component="form" direction={{ xs: 'column', sm: 'row' }} spacing={2} onSubmit={lookup}>
                <TextField
                    label="NetSuite customer number"
                    value={customerNumber}
                    required
                    disabled={busy}
                    onChange={(event) => {
                        setCustomerNumber(event.target.value)
                        setCustomers(null)
                    }}
                />
                <Button type="submit" variant="contained" disabled={busy || !customerNumber.trim()}>
                    {lookingUp ? 'Looking up…' : 'Find customer'}
                </Button>
            </Stack>
            {customers?.length === 0 && <Alert severity="info">No NetSuite customer found with that number.</Alert>}
            {customers?.map((customer) => (
                <Paper key={customer.netsuiteId} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                        <Typography variant="h6">{customer.name}</Typography>
                        <Typography>Customer number: {customer.customerNumber}</Typography>
                        <Typography>NetSuite internal ID: {customer.netsuiteId}</Typography>
                        <Typography color="text.secondary">
                            New customers start with Usage based billing. Review billing settings after import.
                        </Typography>
                        <Button
                            variant="contained"
                            disabled={busy}
                            sx={{ alignSelf: 'flex-start' }}
                            onClick={() => void importCustomer(customer)}
                        >
                            {importingId === customer.netsuiteId ? 'Importing…' : 'Import customer'}
                        </Button>
                    </Stack>
                </Paper>
            ))}
            {errorBanner}
        </Stack>
    )
}
